export function formatCurrency(value, decimals = 0) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0';
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1000000) return `${sign}$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(decimals)}`;
}

export function parseDateInput(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d+$/.test(trimmed)) {
      return parseDateInput(Number(trimmed));
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return new Date(`${trimmed}T00:00:00`);
    }
    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      return new Date(trimmed.replace(' ', 'T'));
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function formatLocalDate(value, options = {}, locale) {
  const date = parseDateInput(value);
  if (!date) return '-';
  const formatter = new Intl.DateTimeFormat(locale || undefined, options);
  return formatter.format(date);
}

export function formatLocalTime(value, options = {}, locale) {
  const date = parseDateInput(value);
  if (!date) return '-';
  const formatter = new Intl.DateTimeFormat(locale || undefined, options);
  return formatter.format(date);
}

export function formatLocalDateTime(value, options = {}, locale) {
  const date = parseDateInput(value);
  if (!date) return '-';
  const formatter = new Intl.DateTimeFormat(locale || undefined, options);
  return formatter.format(date);
}

export function toLocalDateKey(value) {
  const date = parseDateInput(value);
  if (!date) return '';
  const yyyy = String(date.getFullYear());
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatGameTimeLabel(
  value,
  {
    todayLabel = 'Today',
    tomorrowLabel = 'Tomorrow',
    locale,
    timeOptions = { hour: '2-digit', minute: '2-digit' },
  } = {}
) {
  const date = parseDateInput(value);
  if (!date) return '';
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const timeText = formatLocalTime(date, timeOptions, locale);
  if (date.toDateString() === now.toDateString()) {
    return `${todayLabel} ${timeText}`;
  }
  if (date.toDateString() === tomorrow.toDateString()) {
    return `${tomorrowLabel} ${timeText}`;
  }
  const dateText = formatLocalDate(date, { month: 'numeric', day: 'numeric' }, locale);
  return `${dateText} ${timeText}`;
}

export function formatMaybeTime(value, { withSeconds = true, locale } = {}) {
  if (!value) return '';
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      return trimmed;
    }
  }
  const date = parseDateInput(value);
  if (!date) return '';
  const options = { hour: '2-digit', minute: '2-digit' };
  if (withSeconds) {
    options.second = '2-digit';
  }
  return formatLocalTime(date, options, locale);
}

export function formatPercent(value, decimals = 1) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${(num * 100).toFixed(decimals)}%`;
}

export function formatOdds(probability) {
  if (probability >= 0.5) {
    return `-${Math.round((probability / (1 - probability)) * 100)}`;
  }
  return `+${Math.round(((1 - probability) / probability) * 100)}`;
}

export function formatTimeAgo(timestamp) {
  const date = parseDateInput(timestamp);
  if (!date) return '';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

export function shortenAddress(address, chars = 4) {
  if (!address) return '';
  return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
}
